import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import ModaleLocale from "../../../common/CustomModal/ModaleLocale";
import ChoixCartes from "./ChoixCartes";
import { ACCOMPAGNEMENTS, corpsCorrection, erreursCorrection, optionsCorrection } from "./accompagnement";
import { corrigerSousOrientation } from "./sousOrientationApi";

// ============================================================
// Modale « Modifier l'accompagnement » (ex-« Corriger », CE-1370, maquettes 11
// et 12) : vers la Caf ou un opérateur que l'API propose, avec un motif
// obligatoire. Une orientation manuelle peut changer d'accompagnement ; une
// sous-orientation automatique reste en total (CE-1414). Les erreurs de
// l'écran et celles de l'API s'affichent sous leur champ.
// ============================================================
const ModaleCorrection = ({ allocataireId, nomAllocataire, so, onFermer, onCorrigee }) => {
  const manuelle = so.origine === "MANUELLE";
  const [accompagnement, setAccompagnement] = useState(so.accompagnement || "TOTAL");
  const [vers, setVers] = useState(null);
  const [motif, setMotif] = useState("");
  const [erreurs, setErreurs] = useState({});
  const [refus, setRefus] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const refGroupe = useRef(null);
  const refMotif = useRef(null);
  // Le dossier peut être fermé (navigation) pendant l'enregistrement.
  const monte = useRef(true);
  useEffect(
    () => () => {
      monte.current = false;
    },
    []
  );
  const options = optionsCorrection(so, accompagnement);

  const enregistrer = () => {
    const ecran = erreursCorrection({ vers, motif });
    setErreurs(ecran);
    setRefus(null);
    if (Object.keys(ecran).length > 0) {
      if (ecran.vers) {
        const premier = refGroupe.current && refGroupe.current.querySelector("input:not([disabled])");
        if (premier) premier.focus();
      } else if (refMotif.current) {
        refMotif.current.focus();
      }
      return;
    }
    setEnCours(true);
    corrigerSousOrientation(allocataireId, corpsCorrection(vers, motif, manuelle ? accompagnement : null)).then(
      (relue) => {
        toast.success("Modification enregistrée dans l'historique.");
        onCorrigee(relue);
      },
      (err) => {
        if (!monte.current) return;
        setEnCours(false);
        if (err && err.champs) {
          setErreurs({ accompagnement: err.champs.accompagnement, vers: err.champs.operateurId, motif: err.champs.motif });
        } else {
          setRefus((err && err.message) || "La modification n'a pas pu être enregistrée.");
        }
      }
    );
  };

  const pied = (
    <div className="correction-so__pied">
      <button type="button" className="btn btn-ghost" onClick={onFermer} disabled={enCours}>
        Annuler
      </button>
      <button type="button" className="btn btn-primary" onClick={enregistrer} disabled={enCours}>
        Enregistrer la modification
      </button>
    </div>
  );

  return (
    <ModaleLocale
      ouvert
      onFermer={onFermer}
      titre="Modifier l'accompagnement"
      sousTitre={nomAllocataire}
      taille="etroite"
      idTitre="modale-correction-titre"
      peutFermer={!enCours}
      pied={pied}
    >
      <div className="correction-so">
        {refus && (
          <div className="alert alert-danger" role="alert">
            {refus}
          </div>
        )}
        {manuelle && (
          <ChoixCartes
            nom="accompagnement"
            legende="Accompagnement"
            options={ACCOMPAGNEMENTS}
            valeur={accompagnement}
            onChoisir={(valeur) => {
              setAccompagnement(valeur);
              // Les opérateurs proposés changent avec l'accompagnement.
              setVers(null);
              setErreurs((e) => ({ ...e, accompagnement: undefined }));
            }}
            idErreur="erreur-correction-accompagnement"
            erreur={erreurs.accompagnement}
          />
        )}
        <ChoixCartes
          nom="correction-vers"
          legende="Vers"
          options={options}
          valeur={vers}
          onChoisir={(valeur) => {
            setVers(valeur);
            setErreurs((e) => ({ ...e, vers: undefined }));
          }}
          idAide="aide-correction-vers"
          aide={manuelle ? null : "Une sous-orientation automatique reste en accompagnement total."}
          idErreur="erreur-correction-vers"
          erreur={erreurs.vers}
          refGroupe={refGroupe}
        />
        <div className="correction-so__champ">
          <label htmlFor="correction-motif">
            Motif <span className="correction-so__requis" aria-hidden="true">*</span>
          </label>
          <textarea
            id="correction-motif"
            ref={refMotif}
            className="form-control"
            rows={3}
            value={motif}
            onChange={(e) => {
              const valeur = e.target.value;
              setMotif(valeur);
              if (valeur.trim()) setErreurs((courantes) => ({ ...courantes, motif: undefined }));
            }}
            aria-invalid={erreurs.motif ? "true" : undefined}
            aria-describedby={`aide-correction-motif${erreurs.motif ? " erreur-correction-motif" : ""}`}
          />
          <p className="correction-so__aide" id="aide-correction-motif">
            Il reste dans l'historique de l'allocataire, avec votre nom et la date.
          </p>
          {erreurs.motif && (
            <p className="correction-so__erreur" id="erreur-correction-motif" role="alert">
              {erreurs.motif}
            </p>
          )}
        </div>
      </div>
    </ModaleLocale>
  );
};

export default ModaleCorrection;
